import React, { useState, useEffect } from "react";
import { FileTreeItem } from "../file/tree";
import { FileTypeIcon } from "./FileTypeIcon";
import { useIsCompact } from "../core/useBreakpoints";

const EDITOR_FONT = "'Comic Mono', 'Ubuntu', Consolas, 'Courier New', monospace";
const ITEM_HEIGHT = 28;
/* A 28px row is below the ~44px comfortable tap target on touch screens. */
const ITEM_HEIGHT_COMPACT = 38;

export function FileListItem({
    item,
    select,
    depth = 0,
    selectedPath,
    forceExpanded = false,
}: {
    item: FileTreeItem;
    select: (item: FileTreeItem) => void;
    depth: number;
    selectedPath?: string;
    forceExpanded?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [expandChildren, setExpandChildren] = useState(false);
    const [hovered, setHovered] = useState(false);
    const isCompact = useIsCompact();
    const rowHeight = isCompact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT;

    const isDir = item.type === "dir";
    const isSelected = !isDir && selectedPath === item.path;

    // When a parent expands us, open and cascade down to our children
    useEffect(() => {
        if (forceExpanded && isDir) {
            setOpen(true);
            setExpandChildren(true);
        }
    }, [forceExpanded, isDir]);

    const handleClick = () => {
        if (isDir) {
            const next = !open;
            setOpen(next);
            // Expand all descendants when opening; reset when closing
            setExpandChildren(next);
        } else {
            select(item);
        }
    };

    const rowBg = isSelected
        ? "#094771"
        : hovered
        ? "#2a2d2e"
        : "transparent";

    return (
        <>
            <div
                onClick={handleClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    height: rowHeight,
                    paddingLeft: depth * 12 + 8,
                    paddingRight: 8,
                    cursor: "pointer",
                    backgroundColor: rowBg,
                    color: "#cccccc",
                    fontSize: isCompact ? 15 : 18,
                    fontFamily: EDITOR_FONT,
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {/* Folder expand arrow */}
                <span
                    style={{
                        width: 16,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        color: "#c5c5c5",
                        fontSize: 12,
                    }}
                >
                    {isDir ? (open ? "▾" : "▸") : ""}
                </span>
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        marginRight: 6,
                        flexShrink: 0,
                    }}
                >
                    <FileTypeIcon type={item.type} name={item.name} open={open} />
                </span>
                {/* Name */}
                <span
                    style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flexShrink: 1,
                    }}
                >
                    {item.name}
                </span>
            </div>

            {/* Children */}
            {isDir && open && (
                <div>
                    {item.children && item.children.length > 0 ? (
                        item.children.map((child, idx) => (
                            <FileListItem
                                key={idx}
                                item={child}
                                select={select}
                                depth={depth + 1}
                                selectedPath={selectedPath}
                                forceExpanded={expandChildren}
                            />
                        ))
                    ) : (
                        <div
                            style={{
                                paddingLeft: (depth + 1) * 12 + 24,
                                height: rowHeight,
                                lineHeight: `${rowHeight}px`,
                                color: "#858585",
                                fontSize: isCompact ? 13 : 16,
                                fontStyle: "italic",
                                fontFamily: EDITOR_FONT,
                            }}
                        >
                            (empty)
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
